import React from "react";
import { getSegmentListKey2, SurveySegment } from "../../../../../../amorg-common";
import { Flex } from "../../../../../../designSystem/compositeComponents/FlexHelpers";
import { OnOff } from "../../../../../../designSystem/inputs/OnOff";
import { Table } from "../../../../../../designSystem/Table";
import { SegmentationViewMode } from "../../../../../../utils/amorgLocalStorage";
import { SegmentListChip } from "./SegmentListChip";
import { InfoChip } from "../../../../../../designSystem/Chip";

export type SegmentListViewData = {
    segments: SurveySegment[],
    selected: boolean,
    answerCount: number;
};

type SegmentListViewProps = {
    segmentLists: SegmentListViewData[],
    displayAnswerCount?: boolean,
    noSelectionContent?: React.ReactNode,
    localStorageViewModeKey?: string,
    onToggleSelect?: (identifier: string, selected: boolean) => void,
    onToggleAll?(): void
    view?: SegmentationViewMode
};

export function SegmentListView (props: SegmentListViewProps): JSX.Element 
{
    const keyedSegmentLists = React.useMemo(() => {
        return props.segmentLists.map((sl) => ({
            ...sl,
            identifier: getSegmentListKey2(sl.segments)
        }));
    }, [props.segmentLists]);

    return (
        <>
            {props.segmentLists.length > 0 &&
                <Flex flexDirection="column" gap={16}>
                    
                    {props.segmentLists.length > 0 && props.view === 'pills' &&
                        <SegmentListChipsView segmentLists={keyedSegmentLists}
                            displayAnswerCount={props.displayAnswerCount}
                            onToggleSelect={props.onToggleSelect}
                            onToggleAll={props.onToggleAll} />
                    }

                    {props.segmentLists.length > 0 && props.view === 'table' &&
                        <SegmentListTableView segmentLists={keyedSegmentLists}
                            displayAnswerCount={props.displayAnswerCount}
                            onToggleSelect={props.onToggleSelect}
                            onToggleAll={props.onToggleAll} />
                    }

                    {props.segmentLists.length === 0 &&
                        <Flex style={{ fontSize: '16px', paddingBottom: '24px' }} justifyContent="center">
                            {props.noSelectionContent || <i>Der er ingen segmentlister</i>}
                        </Flex>
                    }

                </Flex>
            }
        </>
    );
}

type InternalSegmentListChipsViewProps = {
    segmentLists:{
        identifier: string,
        segments: SurveySegment[],
        selected: boolean,
        answerCount: number
    }[],
    displayAnswerCount?: boolean,
    onToggleSelect?: (identifier: string, selected: boolean) => void,
    onToggleAll?: () => void
}

function SegmentListChipsView (props: InternalSegmentListChipsViewProps): JSX.Element
{
    function handleClick (identifier: string, selected: boolean)
    {
        if (props.onToggleSelect)
            props.onToggleSelect(identifier, selected);
    }

    const shownSegmentLists = React.useMemo(() => {
        if (props.onToggleSelect) {
            return props.segmentLists;
        }
        else {
            return props.segmentLists.filter(sl => sl.selected);
        }
    }, [props.segmentLists, props.onToggleSelect]);

    return (
        <section>
            <Flex gap={4} style={{ flexWrap: 'wrap' }}>
                {shownSegmentLists.map((l, index) =>
                    <SegmentListChip key={l.identifier + '_' + index}
                        identifier={l.identifier}
                        segments={l.segments}
                        selected={l.selected}
                        answerCount={props.displayAnswerCount ? l.answerCount : undefined}
                        onClick={props.onToggleSelect ? handleClick : undefined} />
                )}
            </Flex>
        </section>
    );
}

function SegmentListTableView (props: InternalSegmentListChipsViewProps): JSX.Element
{
    if (!props.segmentLists)
        return <></>;

    function handleToggleClick (identifier: string, selected: boolean)
    {
        if (props.onToggleSelect)
            props.onToggleSelect(identifier, selected);
    }

    return (
        <>
            <Table style={{ overflowX: 'auto' }}>
                <thead>
                    <tr>
                        <th style={{ width: '40px' }}>
                            {props.onToggleSelect && props.onToggleAll &&
                                <OnOff size="small" active={props.segmentLists.every(s => s.selected)} onClick={props.onToggleAll} />
                            }
                        </th>
                        <th>Segmenter</th>
                        {props.displayAnswerCount &&
                            <th>Antal besvarelser</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    {props.segmentLists.map(sl =>
                        <tr key={sl.identifier}>
                            <td>
                                {props.onToggleSelect &&
                                    <OnOff size="small" active={sl.selected} onClick={() => handleToggleClick(sl.identifier, !sl.selected)} />
                                }
                            </td>
                            <td>
                                <Flex gap={4} style={{ flexWrap: 'wrap' }}>
                                    {sl.segments.map((s, index) =>
                                        <InfoChip key={sl.identifier + '-' + index} variant={sl.selected ? 'filled' : 'outlined'}>
                                            {s.value}
                                        </InfoChip>
                                    )}

                                </Flex>

                            </td>
                            {props.displayAnswerCount &&
                                <td>{sl.answerCount}</td>
                            }
                        </tr>
                    )}
                </tbody>
            </Table >
        </>
    );
}